import styled, { css, DefaultTheme } from 'styled-components';
import { Anchor, DialogStyledProps } from './Dialog.typed';
import {
  BreakpointValue,
  DialogSizeValue,
  forEachBreakpoint,
} from 'styled-system';
import { TransitionStatus } from 'react-transition-group';

const MAX_WIDTH_CENTER = (size: DialogSizeValue) => css`
  width: ${(props) => props.theme.components.dialog.sizes[size]};
`;

const MAX_HEIGHT_CENTER = css`
  // backup of 100% if min function isn't supported
  max-height: 100%;
  max-height: min(640px, 100%);
`;
const MAX_WIDTH_LEFT_OR_RIGHT = (size: DialogSizeValue) => css`
  width: ${(props) => props.theme.components.dialog.sizes[size]};
`;

const MAX_HEIGHT_TOP_OR_BOTTOM = css`
  // backup of 100% if min function isn't supported
  max-height: 100%;
  max-height: min(536px, 100%);
`;

const horizontalAnchor = (
  state: TransitionStatus,
  size: DialogSizeValue,
) => css`
  top: 0;
  bottom: 0;
  ${MAX_WIDTH_LEFT_OR_RIGHT(size)};
  max-height: none;
  transform: none;
  ${state === 'entered' &&
  css`
    transform: translateX(0) !important;
  `}
`;
const leftAnchor = (state: TransitionStatus, size: DialogSizeValue) => css`
  left: 0;
  right: auto;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  transform: translateX(-100%) !important;
  ${horizontalAnchor(state, size)};
`;
const rightAnchor = (state: TransitionStatus, size: DialogSizeValue) => css`
  right: 0;
  left: auto;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  transform: translateX(100%) !important;
  ${horizontalAnchor(state, size)};
`;
const verticalAnchor = css<{ state: TransitionStatus }>`
  left: 0;
  right: 0;
  max-width: none;
  ${MAX_HEIGHT_TOP_OR_BOTTOM};
  transform: none;  
  ${({ state }) => css`
    ${state === 'entered' &&
    css`
      transform: translateY(0) !important;
    `}
  `}}
`;
const topAnchor = css`
  top: 0;
  bottom: auto;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  transform: translateY(-100%) !important;
  ${verticalAnchor};
`;
const bottomAnchor = css`
  bottom: 0;
  top: auto;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  transform: translateY(100%) !important;
  ${verticalAnchor};
`;
const centerAnchor = (state: TransitionStatus, size: DialogSizeValue) => css`
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  ${MAX_WIDTH_CENTER(size)};
  ${MAX_HEIGHT_CENTER};
  transform: translate(-50%, -50%);
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.m};
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.m};
  opacity: 0;
  ${state === 'entered' &&
  css`
    opacity: 1 !important;
  `}
`;

const getAnchorStyles = (
  anchor: Anchor,
  size: DialogSizeValue,
  state: TransitionStatus,
) => {
  switch (anchor) {
    case 'bottom':
      return bottomAnchor;
    case 'top':
      return topAnchor;
    case 'left':
      return leftAnchor(state, size);
    case 'right':
      return rightAnchor(state, size);
    default:
      return centerAnchor(state, size);
  }
};

const getAnchorBreakpoints = (
  anchor: DialogStyledProps['anchor'],
  theme: DefaultTheme,
  size: DialogSizeValue,
  state: TransitionStatus,
) => {
  const sortedAnchors: BreakpointValue[] = [];
  forEachBreakpoint((breakpoint) => {
    if (!anchor[breakpoint]) {
      return;
    }
    sortedAnchors.push(breakpoint);
  });
  const keyCount = sortedAnchors.length;

  return sortedAnchors.map((key, index) => {
    if (!anchor[key]) {
      return null;
    }
    const isLast = index === keyCount - 1;
    const nextKey = isLast ? null : sortedAnchors[index + 1];
    const isSmallest = key === 'xs';

    if (isSmallest && isLast) {
      return css`
        ${getAnchorStyles(anchor[key]!, size, state)}
      `;
    } else if (isSmallest && !isLast) {
      return css`
        @media screen and (max-width: ${theme.breakpoints[
            nextKey as BreakpointValue
          ]}px) {
          ${getAnchorStyles(anchor[key]!, size, state)}
        }
      `;
    } else if (!isLast) {
      return css`
        @media screen and (min-width: ${theme.breakpoints[
            key as BreakpointValue
          ]}px) and (max-width: ${theme.breakpoints[
            nextKey as BreakpointValue
          ]}px) {
          ${getAnchorStyles(anchor[key]!, size, state)}
        }
      `;
    } else {
      return css`
        @media screen and (min-width: ${theme.breakpoints[
            key as BreakpointValue
          ]}px) {
          ${getAnchorStyles(anchor[key]!, size, state)}
        }
      `;
    }
  });
};

export const Backdrop = styled.div<{ state: TransitionStatus }>`
  position: fixed;
  //z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #0008;
  opacity: 0;
  transition: opacity 0.5s;
  will-change: opacity;
  ${({ state }) => css`
    ${state === 'entered' &&
    css`
      opacity: 1;
    `}
  `}}
`;

export const Dialog = styled.div<DialogStyledProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  overflow: auto;
  background: #ffffff;
  border-radius: ${({ theme }) => theme.borderRadius.m};
  box-shadow: ${({ theme }) => theme.shadow.primary};

  ${({ anchor, size, theme, state }) =>
    getAnchorBreakpoints(anchor, theme, size, state)}

  // Temp workaround for fullscreen
  ${({ fullScreen }) =>
    fullScreen &&
    css`
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
      transform: none !important;
      border-radius: 0 !important;
    `}
  ${({ animationTimeInMS }) => css`
    transition: transform ${animationTimeInMS}ms;
  `}
  will-change: transform;
`;

export const Header = styled.header`
  position: sticky;
  z-index: 2;
  top: 0;
  flex: 0 0 auto;
  display: flex;
  align-items: start;
  justify-content: space-between;
  font-weight: bold;
  background: #ffffff;
  padding: ${({ theme }) =>
    `${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.S8}`};

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: ${({ theme }) =>
      `${theme.spacing.M24} ${theme.spacing.M24} ${theme.spacing.S8}`};
  }

  &:only-child {
    margin-bottom: ${({ theme }) => theme.spacing.M16};
  }
`;

export const HeaderContent = styled.div`
  flex: 1 1 auto;
`;

export const Close = styled.button`
  flex: 0 0 auto;
  margin-left: 0.5rem;
`;

export const Body = styled.div`
  position: relative;
  flex: 1 0 auto;
  padding: ${({ theme }) => `0 ${theme.spacing.M16}`};

  &:first-child {
    padding-top: ${({ theme }) => theme.spacing.M16};
  }
  &:last-child {
    padding-bottom: ${({ theme }) => theme.spacing.M16};
  }

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: ${({ theme }) => `0 ${theme.spacing.M24}`};

    &:first-child {
      padding-top: ${({ theme }) => theme.spacing.M24};
    }
    &:last-child {
      padding-bottom: ${({ theme }) => theme.spacing.M24};
    }
  }
`;

export const Footer = styled.footer`
  position: sticky;
  z-index: 2;
  bottom: 0;
  flex: 0 0 auto;
  padding: ${({ theme }) =>
    `${theme.spacing.S8} ${theme.spacing.M16} ${theme.spacing.M24}`};
  border-radius: ${({ theme }) => theme.borderRadius.m};

  @media only screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: ${({ theme }) =>
      `${theme.spacing.S8} ${theme.spacing.M24} ${theme.spacing.M24}`};
  }

  background: white;

  &:only-child {
    margin-top: ${({ theme }) => theme.spacing.M16};
  }
`;

export const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  grid-gap: ${({ theme }) => theme.spacing.S8};
`;
